import React from 'react';
import { formatCostUsd } from '../../utils/formatCost';
import { formatCostBreakdownDescription } from '../../utils/sessionCostEstimate';
import { normalizeDashboardMessages } from '../../lib/dashboardMessages';
import { formatDate } from './formatters';

export default function SessionTable({
  sessions,
  expandedId,
  onToggle,
  sessionMessages,
  loadingMessagesId,
  emptyMessage,
  loading,
}) {
  if (loading && (!sessions || sessions.length === 0)) {
    return (
      <p className="text-center text-muted py-5 mb-0">
        <i className="fas fa-spinner fa-spin mr-2" />
        Loading conversations…
      </p>
    );
  }

  if (!sessions || sessions.length === 0) {
    return <p className="text-center text-muted py-5 mb-0">{emptyMessage}</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-striped mb-0">
        <thead>
          <tr>
            <th>Email</th>
            <th>Session ID</th>
            <th>Started</th>
            <th>Cost</th>
            <th>Msgs</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => {
            const open = expandedId === s.sessionId;
            const breakdownLine = formatCostBreakdownDescription(s.costBreakdown);
            const msgs = normalizeDashboardMessages(
              sessionMessages[s.sessionId] ?? s.messages ?? []
            );
            const msgCount = msgs.length || s.messageCount || 0;
            return (
              <React.Fragment key={s.sessionId}>
                <tr
                  className="session-toggle"
                  onClick={() => onToggle(s.sessionId, open)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggle(s.sessionId, open);
                    }
                  }}
                >
                  <td>
                    <strong>{s.userEmail || 'Unknown user'}</strong>
                    {!s.endedAt && (
                      <span className="badge badge-warning ml-2">In progress</span>
                    )}
                  </td>
                  <td className="text-sm text-muted">{s.sessionId || '—'}</td>
                  <td className="text-sm">
                    {formatDate(s.startedAt)}
                    {s.endedAt && (
                      <>
                        <br />
                        <span className="text-muted">→ {formatDate(s.endedAt)}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <span className="text-success font-weight-bold">
                      {formatCostUsd(s.sessionCostUsd)}
                    </span>
                  </td>
                  <td>{msgCount}</td>
                  <td className="text-center">
                    <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-muted`} />
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={6} className="bg-light p-3">
                      {breakdownLine && (
                        <p className="text-muted text-sm mb-3 border-bottom pb-2">
                          <strong>Cost breakdown:</strong> {breakdownLine}
                        </p>
                      )}
                      {loadingMessagesId === s.sessionId && (
                        <p className="text-muted text-sm">
                          <i className="fas fa-spinner fa-spin mr-1" />
                          Loading messages…
                        </p>
                      )}
                      {msgs.length > 0 ? (
                        msgs.map((m, i) => (
                          <div
                            key={`${s.sessionId}-${i}`}
                            className={`message-bubble ${m.role === 'user' ? 'message-user' : ''}`}
                          >
                            <span className="badge badge-light text-uppercase mb-1">
                              {m.role === 'user' ? (s.userEmail || 'User') : 'Mr. Berwind'}
                            </span>
                            <div>{m.content || '(empty)'}</div>
                          </div>
                        ))
                      ) : (
                        loadingMessagesId !== s.sessionId && (
                          <p className="text-muted text-sm mb-0">
                            {msgCount > 0
                              ? 'Transcript not in the database. Use Sync from Sheet or npm run import-sheets; new chats save correctly after the latest server update.'
                              : 'No messages recorded for this session.'}
                          </p>
                        )
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
